import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Ticket, CheckCircle2, Clock, Receipt } from 'lucide-react';
import api from '../../../services/api';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import Modal from '../../../components/shared/Modal/Modal';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import { createBookingGroup, groupMarkPaid, groupSendStk, paymentMethodLabel } from '../../../services/bookings';
import { getTripDetail, isBookableTrip } from '../../../services/trips';
import { getRoute } from '../../../services/routes';
import type { Booking, Route, Seat, Trip } from '../../../types/api';
import { useAuth } from '../../../context/AuthContext';
import { seatLabel, bookingSeatText } from '../../../utils/seatLabel';

// Label shown in the searchable trip dropdown. Prefer route name → origin →
// destination → id. Departure time is included so operators can distinguish
// two trips on the same route.
const tripLabel = (t: Trip): string => {
  const route =
    t.route_name ||
    [t.origin_name, t.destination_name].filter(Boolean).join(' → ') ||
    `Route #${t.route}`;
  const when = t.departure_time
    ? new Date(t.departure_time).toLocaleString([], {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';
  const seats =
    typeof t.available_seats === 'number' ? ` · ${t.available_seats} left` : '';
  return `#${t.id} · ${route}${when ? ` · ${when}` : ''}${seats}`;
};

type PendingAction = { type: 'cancel' | 'refund'; booking: Booking } | null;

interface BookingForm {
  trip: string;
  passenger_name: string; passenger_phone: string; passenger_id_number: string;
}
const emptyBooking: BookingForm = {
  trip: '',
  passenger_name: '', passenger_phone: '', passenger_id_number: '',
};

// Persisted in sessionStorage so navigating away and back keeps the operator
// on the same day. Cleared when the browser tab closes so a fresh session
// starts from all-dates.
const TRIP_DATE_KEY = 'bookings.tripDate';

const Bookings: React.FC = () => {
  const { user } = useAuth();
  const isManagement = user?.role === 'admin' || user?.role === 'transport_manager' || user?.role === 'accountant';

  // View filter: bookings whose trip departs on this date ('' = all dates).
  const [tripDate, setTripDate] = useState<string>(() => {
    try { return sessionStorage.getItem(TRIP_DATE_KEY) ?? ''; }
    catch { return ''; }
  });
  useEffect(() => {
    try {
      if (tripDate) sessionStorage.setItem(TRIP_DATE_KEY, tripDate);
      else sessionStorage.removeItem(TRIP_DATE_KEY);
    } catch { /* private mode / disabled storage — filter still works in memory */ }
  }, [tripDate]);
  const { data: bookings, loading, refetch } = usePaginatedResource<Booking>(
    '/bookings/', tripDate ? { trip_date: tripDate } : undefined);

  // Trips list for the "Manual booking" dropdown.
  const { data: trips, loading: tripsLoading } = usePaginatedResource<Trip>('/trips/');

  const markPaid = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/mark_paid/`); },
    { successMessage: 'Booking marked paid' }
  );
  const cancelBooking = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/cancel/`); },
    { successMessage: 'Booking cancelled' }
  );
  const refundBooking = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/refund/`); },
    { successMessage: 'Booking refunded' }
  );
  const sendStk = useResourceMutation(
    async ({ id, phone }: { id: number; phone: string }) => {
      await api.post(`/bookings/${id}/send_stk/`, { phone });
    },
    { successMessage: 'Payment request sent' }
  );

  const [pending, setPending] = useState<PendingAction>(null);
  const [stkTarget, setStkTarget] = useState<Booking | null>(null);
  const [stkPhone, setStkPhone] = useState('');
  // Group STK: { groupId, defaultPhone }
  const [groupStkTarget, setGroupStkTarget] = useState<{ groupId: number; defaultPhone: string } | null>(null);
  const [groupStkPhone, setGroupStkPhone] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState<BookingForm>(emptyBooking);
  // Multi-seat selection for the booking dialog
  const [selectedDialogSeatIds, setSelectedDialogSeatIds] = useState<number[]>([]);

  // Trip detail state for boarding/alighting stop dropdowns
  const [tripDetail, setTripDetail] = useState<Trip | null>(null);
  const [routeForTrip, setRouteForTrip] = useState<Route | null>(null);
  const [tripDetailLoading, setTripDetailLoading] = useState(false);
  const [boardingStopId, setBoardingStopId] = useState<number | null>(null);
  const [alightingStopId, setAlightingStopId] = useState<number | null>(null);

  const selectedTripId = bookingForm.trip ? parseInt(bookingForm.trip, 10) : null;

  // Only offer trips someone could actually book on: upcoming (not departed on
  // an earlier day), not cancelled / completed, and with at least one free
  // seat. Keeps the dropdown short and avoids operator confusion.
  const tripOptions = useMemo(() => {
    const bookable = trips.filter((t) => isBookableTrip(t));
    // Newest departure first.
    bookable.sort((a, b) =>
      (a.departure_time || '').localeCompare(b.departure_time || ''),
    );
    return bookable.map((t) => ({ value: String(t.id), label: tripLabel(t) }));
  }, [trips]);

  useEffect(() => {
    if (!selectedTripId) {
      setTripDetail(null);
      setRouteForTrip(null);
      setBoardingStopId(null);
      setAlightingStopId(null);
      return;
    }
    let cancelled = false;
    setTripDetailLoading(true);
    (async () => {
      try {
        const td = await getTripDetail(selectedTripId);
        if (cancelled) return;
        setTripDetail(td);
        // route may be expanded with stops in TripDetailSerializer
        if (typeof td.route === 'object' && td.route !== null && (td.route as unknown as Route).stops) {
          setRouteForTrip(td.route as unknown as Route);
        } else {
          const r = await getRoute(td.route as number);
          if (cancelled) return;
          setRouteForTrip(r);
        }
        setBoardingStopId(null);
        setAlightingStopId(null);
      } finally {
        if (!cancelled) setTripDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTripId]);

  const tripStops = routeForTrip?.stops ?? [];
  const boardingSeq = tripStops.find(s => s.stop_id === boardingStopId)?.sequence ?? 0;
  const alightingOptions = tripStops.filter(s => s.sequence > boardingSeq);

  const segmentFare = useMemo(() => {
    if (!boardingStopId || !alightingStopId || !tripDetail?.trip_fares) return null;
    const f = tripDetail.trip_fares.find(
      tf => tf.boarding_stop_id === boardingStopId && tf.alighting_stop_id === alightingStopId,
    );
    return f ? f.amount : null;
  }, [boardingStopId, alightingStopId, tripDetail]);

  const createGroupMut = useResourceMutation(
    (state: BookingForm & { seats: number[]; boarding_stop_id: number; alighting_stop_id: number }) =>
      createBookingGroup({
        trip: parseInt(state.trip, 10),
        seats: state.seats,
        boarding_stop_id: state.boarding_stop_id,
        alighting_stop_id: state.alighting_stop_id,
        passenger_name: state.passenger_name,
        passenger_phone: state.passenger_phone,
        passenger_id_number: state.passenger_id_number,
      }),
    { successMessage: 'Booking created' }
  );

  const groupMarkPaidMut = useResourceMutation(
    async (groupId: number) => { await groupMarkPaid(groupId); },
    { successMessage: 'Group marked paid' }
  );
  const groupSendStkMut = useResourceMutation(
    async ({ groupId, phone }: { groupId: number; phone: string }) => {
      await groupSendStk(groupId, phone);
    },
    { successMessage: 'Payment request sent' }
  );

  const handleMarkPaid = async (b: Booking) => {
    try {
      await markPaid.run(b.id);
      refetch();
    } catch { /* toast handled */ }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.type === 'cancel') await cancelBooking.run(pending.booking.id);
      else await refundBooking.run(pending.booking.id);
      setPending(null);
      refetch();
    } catch { /* toast handled */ }
  };

  const resetBookingDialog = () => {
    setShowBookingForm(false);
    setBookingForm(emptyBooking);
    setTripDetail(null);
    setRouteForTrip(null);
    setBoardingStopId(null);
    setAlightingStopId(null);
    setSelectedDialogSeatIds([]);
  };

  const handleCreateBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!boardingStopId || !alightingStopId || selectedDialogSeatIds.length === 0) return;
    try {
      await createGroupMut.run({ ...bookingForm, seats: selectedDialogSeatIds, boarding_stop_id: boardingStopId, alighting_stop_id: alightingStopId });
      resetBookingDialog();
      refetch();
    } catch { /* toast */ }
  };

  const columns: Column<Booking>[] = [
    { key: 'booking_reference', header: 'Reference', sortable: true },
    { key: 'passenger_name',    header: 'Passenger' },
    { key: 'booked_by_name',    header: 'Booked by' },
    { key: 'passenger_phone',   header: 'Phone' },
    { key: 'trip',              header: 'Trip',
      render: (b) => b.trip_route ?? `#${b.trip}` },
    { key: 'seat',              header: 'Seat',
      render: (b) => bookingSeatText(b) },
    { key: 'amount',            header: 'Amount', align: 'right',
      render: (b) => `KES ${Number(b.amount).toLocaleString()}` },
    { key: 'payment_method',    header: 'Method', filterable: true, filterOptions: [
      { value: 'cash',      label: 'Cash' },
      { value: 'stk',       label: 'M-Pesa STK' },
      { value: 'mpesa_sms', label: 'M-Pesa Paybill' },
    ], render: (b) => paymentMethodLabel(b.payment_method) },
    { key: 'payment_status',    header: 'Payment', filterable: true, filterOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'paid',    label: 'Paid' },
      { value: 'refunded', label: 'Refunded' },
    ], render: (b) => <StatusBadge variant="booking" status={b.payment_status} /> },
    { key: 'created_at',        header: 'Created', sortable: true,
      render: (b) => new Date(b.created_at).toLocaleString() },
  ];

  const rowActions = (b: Booking) => {
    const isGrouped = !!b.group;
    return (
      <div className="inline-flex items-center gap-2">
        {/* Group-level payment actions (shown for grouped pending bookings) */}
        {b.payment_status === 'pending' && isGrouped && (
          <Button
            size="sm"
            onClick={async () => {
              try { await groupMarkPaidMut.run(b.group!); refetch(); } catch { /* toast */ }
            }}
            loading={groupMarkPaidMut.loading}
          >
            Mark group paid
          </Button>
        )}
        {b.payment_status === 'pending' && isGrouped && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setGroupStkTarget({ groupId: b.group!, defaultPhone: b.passenger_phone }); setGroupStkPhone(b.passenger_phone); }}
          >
            Request payment (group)
          </Button>
        )}
        {/* Per-booking actions (only for ungrouped bookings) */}
        {b.payment_status === 'pending' && !isGrouped && (
          <Button size="sm" onClick={() => handleMarkPaid(b)} loading={markPaid.loading}>
            Mark paid
          </Button>
        )}
        {b.payment_status === 'pending' && !isGrouped && (
          <Button variant="secondary" size="sm" onClick={() => { setStkTarget(b); setStkPhone(b.passenger_phone); }}>
            Request payment
          </Button>
        )}
        {b.payment_status === 'pending' && (
          <Button variant="danger" size="sm" onClick={() => setPending({ type: 'cancel', booking: b })}>
            Cancel
          </Button>
        )}
        {b.payment_status === 'paid' && isManagement && (
          <Button variant="secondary" size="sm" onClick={() => setPending({ type: 'refund', booking: b })}>
            Refund
          </Button>
        )}
      </div>
    );
  };

  const stats = useMemo(() => {
    const total = bookings.length;
    const paid = bookings.filter((b) => b.payment_status === 'paid').length;
    const pending = bookings.filter((b) => b.payment_status === 'pending').length;
    const refunded = bookings.filter((b) => b.payment_status === 'refunded').length;
    return { total, paid, pending, refunded };
  }, [bookings]);

  return (
    <>
      <PageHeader
        title="Bookings"
        description="Passenger bookings across all trips."
        actions={
          <Button onClick={() => setShowBookingForm(true)} icon={<Plus className="h-4 w-4" />}>
            New booking
          </Button>
        }
      />

      <StatStrip
        className="mb-6"
        stats={[
          { label: 'Total', value: stats.total, icon: <Ticket className="h-4 w-4" /> },
          { label: 'Paid', value: stats.paid, icon: <CheckCircle2 className="h-4 w-4" />, tone: 'income' },
          { label: 'Pending', value: stats.pending, icon: <Clock className="h-4 w-4" /> },
          { label: 'Refunded', value: stats.refunded, icon: <Receipt className="h-4 w-4" /> },
        ]}
      />

      <div className="mb-4 flex items-end gap-3">
        <div className="w-48">
          <label htmlFor="trip-date-filter" className="mb-1 block text-sm font-medium text-slate-600">
            Trip date
          </label>
          <Input
            id="trip-date-filter"
            type="date"
            value={tripDate}
            onChange={(e) => setTripDate(e.target.value)}
          />
        </div>
        {tripDate && (
          <Button variant="ghost" size="sm" onClick={() => setTripDate('')}>
            Clear
          </Button>
        )}
      </div>

      <DataTable<Booking>
        columns={columns}
        data={bookings}
        rowKey="id"
        loading={loading}
        emptyTitle={tripDate ? 'No bookings for this date' : 'No bookings yet'}
        emptyDescription="Bookings made by passengers will show up here."
        actions={rowActions}
      />

      <Modal
        isOpen={showBookingForm}
        onClose={resetBookingDialog}
        title="Manual booking"
        description="Create a booking on behalf of a passenger."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={resetBookingDialog} disabled={createGroupMut.loading}>Cancel</Button>
            <Button
              onClick={handleCreateBooking}
              loading={createGroupMut.loading}
              disabled={!boardingStopId || !alightingStopId || selectedDialogSeatIds.length === 0}
            >
              Create booking
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateBooking} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Trip" name="trip" required error={createGroupMut.fieldErrors.trip}>
            <SearchableSelect
              id="trip"
              options={tripOptions}
              value={bookingForm.trip}
              onChange={(v) => {
                setBookingForm({ ...bookingForm, trip: v });
                setSelectedDialogSeatIds([]);
              }}
              placeholder={tripsLoading ? 'Loading trips…' : 'Choose a trip…'}
              searchPlaceholder="Search by route, town, or trip #"
              loading={tripsLoading}
              disabled={tripsLoading || tripOptions.length === 0}
              aria-label="Trip"
            />
          </FormField>
          {/* Seat picker — visual grid when trip detail is loaded, plain input otherwise */}
          {(() => {
            const tripSeats: Seat[] = (tripDetail as unknown as { seats?: Seat[] })?.seats ?? [];
            if (tripSeats.length > 0) {
              return (
                <div className="col-span-full">
                  <p className="mb-1.5 text-sm font-medium text-slate-700">
                    Seats <span className="text-rose-500">*</span>
                    {selectedDialogSeatIds.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {selectedDialogSeatIds.length} selected
                        {segmentFare ? ` — KES ${(selectedDialogSeatIds.length * Number(segmentFare)).toLocaleString()}` : ''}
                      </span>
                    )}
                  </p>
                  <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
                    {tripSeats.map((seat) => {
                      const available = seat.status === 'available';
                      const isSelected = selectedDialogSeatIds.includes(seat.id);
                      return (
                        <button
                          key={seat.id}
                          type="button"
                          disabled={!available}
                          onClick={() => {
                            if (!available) return;
                            setSelectedDialogSeatIds((prev) =>
                              prev.includes(seat.id)
                                ? prev.filter((id) => id !== seat.id)
                                : [...prev, seat.id],
                            );
                          }}
                          aria-label={`Seat ${seatLabel(seat.seat_number)}, ${seat.status}${isSelected ? ', selected' : ''}`}
                          className={[
                            'relative flex h-10 w-10 items-center justify-center rounded-lg border text-xs font-bold transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1',
                            isSelected && 'border-brand-700 bg-brand-600 text-white ring-2 ring-brand-700',
                            !isSelected && available && 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400',
                            !isSelected && !available && 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400',
                          ].filter(Boolean).join(' ')}
                        >
                          {seatLabel(seat.seat_number)}
                          {seat.is_window && (
                            <span
                              title="Window seat"
                              aria-label="Window seat"
                              className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-sky-500"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Seat legend */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded border border-emerald-200 bg-emerald-50" aria-hidden />
                      Available
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded border border-brand-700 bg-brand-600" aria-hidden />
                      Selected
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded border border-slate-200 bg-slate-100" aria-hidden />
                      Booked
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-sky-500" aria-hidden />
                      Window
                    </span>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Boarding / alighting stops — shown once a Trip ID is entered */}
          {selectedTripId && (
            <div className="col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Boarding stop" name="boarding_stop_id" required>
                <select
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={boardingStopId ?? ''}
                  disabled={tripDetailLoading || tripStops.length === 0}
                  onChange={(e) => {
                    setBoardingStopId(parseInt(e.target.value, 10) || null);
                    setAlightingStopId(null);
                  }}
                >
                  <option value="">{tripDetailLoading ? 'Loading…' : 'Select…'}</option>
                  {tripStops.map(s => (
                    <option key={s.stop_id} value={s.stop_id}>{s.name}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Alighting stop" name="alighting_stop_id" required>
                <select
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={alightingStopId ?? ''}
                  disabled={!boardingStopId || alightingOptions.length === 0}
                  onChange={(e) => setAlightingStopId(parseInt(e.target.value, 10) || null)}
                >
                  <option value="">Select…</option>
                  {alightingOptions.map(s => (
                    <option key={s.stop_id} value={s.stop_id}>{s.name}</option>
                  ))}
                </select>
              </FormField>
              {segmentFare && (
                <div className="col-span-full rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Fare for this segment: KES {Number(segmentFare).toLocaleString()}
                </div>
              )}
              {boardingStopId && alightingStopId && !segmentFare && (
                <div className="col-span-full rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  No fare configured for this segment — the backend will set the amount.
                </div>
              )}
            </div>
          )}

          <FormField label="Passenger name" name="passenger_name" required error={createGroupMut.fieldErrors.passenger_name}>
            <Input id="passenger_name" required value={bookingForm.passenger_name}
              onChange={(e) => setBookingForm({ ...bookingForm, passenger_name: e.target.value })} />
          </FormField>
          <FormField label="Passenger phone" name="passenger_phone" required error={createGroupMut.fieldErrors.passenger_phone}>
            <Input id="passenger_phone" type="tel" required value={bookingForm.passenger_phone}
              onChange={(e) => setBookingForm({ ...bookingForm, passenger_phone: e.target.value })} />
          </FormField>
          <FormField label="Passenger ID number" name="passenger_id_number" error={createGroupMut.fieldErrors.passenger_id_number}>
            <Input id="passenger_id_number" value={bookingForm.passenger_id_number}
              onChange={(e) => setBookingForm({ ...bookingForm, passenger_id_number: e.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal
        isOpen={!!stkTarget}
        onClose={() => setStkTarget(null)}
        title="Request M-Pesa payment"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setStkTarget(null)} disabled={sendStk.loading}>Cancel</Button>
            <Button
              loading={sendStk.loading}
              disabled={!stkPhone.trim()}
              onClick={async () => {
                if (!stkTarget) return;
                try { await sendStk.run({ id: stkTarget.id, phone: stkPhone.trim() }); setStkTarget(null); }
                catch { /* toast handled by hook */ }
              }}
            >
              Send STK push
            </Button>
          </>
        }
      >
        <FormField label="Payer phone" name="phone" hint="Defaults to the passenger — change it so someone else pays.">
          <Input id="stk-phone" type="tel" value={stkPhone} onChange={(e) => setStkPhone(e.target.value)} />
        </FormField>
      </Modal>

      <Modal
        isOpen={!!groupStkTarget}
        onClose={() => setGroupStkTarget(null)}
        title="Request M-Pesa payment (group)"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setGroupStkTarget(null)} disabled={groupSendStkMut.loading}>Cancel</Button>
            <Button
              loading={groupSendStkMut.loading}
              disabled={!groupStkPhone.trim()}
              onClick={async () => {
                if (!groupStkTarget) return;
                try {
                  await groupSendStkMut.run({ groupId: groupStkTarget.groupId, phone: groupStkPhone.trim() });
                  setGroupStkTarget(null);
                } catch { /* toast handled by hook */ }
              }}
            >
              Send STK push
            </Button>
          </>
        }
      >
        <FormField label="Payer phone" name="group-phone" hint="Defaults to the passenger — change it so someone else pays.">
          <Input id="group-stk-phone" type="tel" value={groupStkPhone} onChange={(e) => setGroupStkPhone(e.target.value)} />
        </FormField>
      </Modal>

      <ConfirmDialog
        isOpen={!!pending}
        title={pending?.type === 'refund' ? 'Refund booking?' : 'Cancel booking?'}
        message={
          pending?.type === 'refund'
            ? `Refund booking ${pending.booking.booking_reference}? The passenger's wallet will be credited.`
            : `Cancel booking ${pending?.booking.booking_reference}? This cannot be undone.`
        }
        confirmLabel={pending?.type === 'refund' ? 'Refund' : 'Cancel booking'}
        variant="danger"
        loading={cancelBooking.loading || refundBooking.loading}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </>
  );
};

export default Bookings;
